import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchServiceBySlug, fetchServices } from '../lib/api';

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [otherServices, setOtherServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website_url: '' // Honeypot
  });
  const [formStatus, setFormStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const data = await fetchServiceBySlug(slug);
        if (!data) {
          // Redirect to 404 if service not found
          navigate('/404', { replace: true });
          return;
        }
        setService(data);

        // Load other services for the sidebar
        const allServices = await fetchServices();
        setOtherServices(allServices.filter(s => s.service_slug !== slug));
      } catch (err) {
        console.error('Error loading service:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [slug, navigate]);

  // Update document metadata
  useEffect(() => {
    if (service) {
      document.title = `${service.title} - Røjsmose Isolering ApS`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', service.summary || '');
      } else {
        const newMeta = document.createElement('meta');
        newMeta.name = 'description';
        newMeta.content = service.summary || '';
        document.head.appendChild(newMeta);
      }
    }
  }, [service]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: null, message: '' });

    // Honeypot check
    if (formData.website_url) {
      // Quietly ignore bot submissions
      setFormStatus({
        type: 'success',
        message: 'Tak for din henvendelse! Vi vender tilbage hurtigst muligt.'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: `[Ydelse: ${service.title}] ${formData.message}`,
          website_url: formData.website_url
        })
      });

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: 'Tak for din henvendelse! Vi har modtaget din besked og vender tilbage hurtigst muligt.'
        });
        setFormData({ name: '', email: '', phone: '', message: '', website_url: '' });
      } else {
        throw new Error('Submission failed');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setFormStatus({
        type: 'error',
        message: 'Der skete en fejl under afsendelsen. Prøv venligst igen, eller ring til os direkte.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-orange border-t-transparent" />
      </div>
    );
  }

  if (!service) return null;

  const serviceImage = service.image?.[0] || 'https://assets.auxodev.dk/assets/4ff98460-cf40-4566-82c8-07bb5181a02e/rojsmose-hero-isolering.jpg';

  return (
    <div className="py-12 sm:py-20 bg-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav className="flex mb-8 text-sm font-medium text-slate-500" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2">
            <li>
              <Link to="/" className="hover:text-brand-orange transition-colors">Forside</Link>
            </li>
            <li className="flex items-center space-x-2">
              <i className="fa-solid fa-chevron-right text-xs text-slate-300" aria-hidden="true" />
              <Link to="/ydelser" className="hover:text-brand-orange transition-colors">Ydelser</Link>
            </li>
            <li className="flex items-center space-x-2 text-slate-900 font-semibold">
              <i className="fa-solid fa-chevron-right text-xs text-slate-300" aria-hidden="true" />
              <span>{service.title}</span>
            </li>
          </ol>
        </nav>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Service Details */}
          <div className="lg:col-span-7 space-y-8">
            <div>
              <h1 className="text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl mt-2">
                {service.title}
              </h1>
            </div>

            {/* Featured Image */}
            <div className="overflow-hidden rounded-none border border-slate-200 bg-slate-50 shadow-sm">
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/${serviceImage.replace('https://assets.auxodev.dk/', '')}`}
                alt={service.title}
                className="w-full h-auto object-cover aspect-[16/10] rounded-none"
              />
            </div>

            {/* Rich Text Content */}
            <div
              className="prose prose-slate max-w-none text-slate-700 leading-relaxed text-lg space-y-6
                prose-h3:text-2xl prose-h3:font-extrabold prose-h3:text-brand-navy prose-h3:mt-8 prose-h3:mb-4
                prose-ul:list-disc prose-ul:pl-6 prose-ul:space-y-2 prose-ul:my-4
                prose-strong:text-brand-navy prose-strong:font-semibold"
              dangerouslySetInnerHTML={{ __html: service.content }}
            />
          </div>

          {/* Right Column: Sidebar */}
          <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-28">
            
            {/* Quick Contact Form Card */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8 shadow-sm">
              <h3 className="text-xl font-bold text-brand-navy mb-2">Få et uforpligtende tilbud</h3>
              <p className="text-sm text-slate-600 mb-6">
                Udfyld formularen, så kontakter vi dig hurtigst muligt for en snak om dit projekt.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot field - absolutely hidden */}
                <div className="sr-only absolute -z-10">
                  <label htmlFor="website_url">Hjemmeside URL</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleInputChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
                    Navn / Firma <span className="text-brand-orange">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-slate-300 bg-transparent px-1 py-2 text-sm text-slate-900 focus:border-brand-orange focus:outline-none focus:ring-0"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
                      E-mail <span className="text-brand-orange">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-slate-300 bg-transparent px-1 py-2 text-sm text-slate-900 focus:border-brand-orange focus:outline-none focus:ring-0"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
                      Telefon <span className="text-brand-orange">*</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-slate-300 bg-transparent px-1 py-2 text-sm text-slate-900 focus:border-brand-orange focus:outline-none focus:ring-0"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
                    Besked <span className="text-brand-orange">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows="4"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder={`Hej, jeg vil gerne have et tilbud på ${service.title.toLowerCase()}...`}
                    className="w-full rounded-none border-b border-t-0 border-l-0 border-r-0 border-slate-300 bg-transparent px-1 py-2 text-sm text-slate-900 focus:border-brand-orange focus:outline-none focus:ring-0"
                  />
                </div>

                {formStatus.message && (
                  <div
                    className={`p-4 rounded-md text-sm font-medium ${
                      formStatus.type === 'success'
                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                        : 'bg-rose-50 text-rose-800 border border-rose-200'
                    }`}
                  >
                    {formStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center rounded-full bg-brand-orange px-5 py-3 text-sm font-bold text-white shadow-sm transition-all duration-200 hover:bg-brand-orange-hover hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fa-solid fa-spinner-third animate-spin mr-2" aria-hidden="true" />
                      Sender...
                    </>
                  ) : (
                    'Send forespørgsel'
                  )}
                </button>
              </form>
            </div>

            {/* Other Services Navigation */}
            {otherServices.length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
                <h3 className="text-lg font-bold text-brand-navy mb-4">Vores andre ydelser</h3>
                <nav className="space-y-2">
                  {otherServices.map((s) => (
                    <Link
                      key={s.id}
                      to={`/ydelser/${s.service_slug}`}
                      className="block p-3 rounded-lg border border-slate-100 bg-slate-50 text-sm font-semibold text-brand-navy transition-all duration-200 hover:border-brand-orange/20 hover:bg-orange-50/30 hover:text-brand-orange"
                    >
                      {s.title}
                    </Link>
                  ))}
                </nav>
              </div>
            )}

            {/* Direct Contact Info */}
            <div className="rounded-2xl border border-slate-200 bg-brand-navy p-6 sm:p-8 text-white shadow-sm relative overflow-hidden">
              <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#f97316_1px,transparent_1px)] [background-size:12px_12px]" />
              <div className="relative z-10">
                <h3 className="text-lg font-bold mb-4">Direkte kontakt</h3>
                <div className="space-y-4 text-sm">
                  <a href="tel:42585848" className="flex items-center gap-3 hover:text-brand-orange transition-colors">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-brand-orange">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <span>42 58 58 48</span>
                  </a>
                  <a href="mailto:iso@rojsmose.dk" className="flex items-center gap-3 hover:text-brand-orange transition-colors">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-brand-orange">
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                    </div>
                    <span>iso@rojsmose.dk</span>
                  </a>
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-brand-orange">
                      <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    </div>
                    <span>Jennyvænget 23, 5270 Odense N</span>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}
